import axios, {
    type AxiosError,
    type AxiosInstance,
    type AxiosRequestConfig,
    type AxiosRequestHeaders,
    type AxiosResponse,
    type ResponseType
} from 'axios';

/* eslint-disable @typescript-eslint/no-explicit-any */
declare module 'axios' {
    export interface AxiosRequestConfig {
        topMostError?: Error;
    }
    export interface AxiosError {
        originalStack?: string;
    }
}

export type QueryParamsType = Record<string | number, any>;

export interface FullRequestParams extends Omit<AxiosRequestConfig, 'data' | 'params' | 'url' | 'responseType'> {
    /** set parameter to `true` for call `securityWorker` for this request */
    secure?: boolean;
    /** request path */
    path: string;
    /** content type of request body */
    type?: ContentType;
    /** query params */
    query?: QueryParamsType;
    /** format of response (i.e. response.json() -> format: "json") */
    format?: ResponseType;
    /** request body */
    body?: unknown;
}

export type RequestParams = Omit<FullRequestParams, 'body' | 'method' | 'query' | 'path'>;

export interface IApiConfig extends Omit<AxiosRequestConfig, 'data' | 'cancelToken'> {
    format?: ResponseType;
    successHandler?: <T = any>(response: AxiosResponse<any, any>) => T;
    onResponseHeaders?: (headers: AxiosResponse['headers']) => void;
}

export type ContentType = 'application/json' | 'multipart/form-data' | 'application/x-www-form-urlencoded';

interface IErrorBase<E> {
    type: 'axios-error' | 'stock-error';
    error: Error | E;
}

interface IAxiosError<E> extends IErrorBase<E> {
    error: AxiosError<E>;
    type: 'axios-error';
}

interface IStockError<E> extends IErrorBase<E> {
    error: Error;
    type: 'stock-error';
}

export class HttpClient {
    public instance: AxiosInstance;
    private format?: ResponseType;
    private successHandler?: <T = any>(response: AxiosResponse) => T;
    private onResponseHeaders?: (headers: AxiosResponse['headers']) => void;

    static setDefaults = (baseurl: string, headers: Record<string, string>) => {
        // default uri
        axios.defaults.baseURL = baseurl;
        axios.defaults.withCredentials = true;

        // default header
        if (headers) {
            Object.entries(headers).forEach(([key, value]) => {
                axios.defaults.headers.common[key] = value;
            });
        }
    };

    constructor({ format, successHandler, onResponseHeaders, ...axiosConfig }: IApiConfig = {}) {
        this.instance = axios.create({ ...axiosConfig });
        this.format = format;
        this.successHandler = successHandler;
        this.onResponseHeaders = onResponseHeaders;
    }

    private createFormData(input: Record<string, unknown>): FormData {
        return Object.keys(input ?? {}).reduce((formData, key) => {
            const property = input[key];
            formData.append(
                key,
                property instanceof Blob
                    ? property
                    : typeof property === 'object' && property !== null
                    ? JSON.stringify(property)
                    : `${property}`
            );
            return formData;
        }, new FormData());
    }

    private axiosErrorHandler = <T>(callback: (err: IAxiosError<T> | IStockError<T>) => void) => {
        return (error: Error | AxiosError<T>) => {
            if (axios.isAxiosError(error)) {
                if (error.response?.data) {
                    callback({
                        error: error as AxiosError<T>,
                        type: 'axios-error'
                    });
                } else {
                    /** no data provided by the server */
                    callback({ error: error, type: 'stock-error' });
                }
            } else {
                callback({
                    error: error,
                    type: 'stock-error'
                });
            }
        };
    };

    public request = async <T = any, E = any>({
        path,
        type,
        query,
        format,
        body,
        ...params
    }: FullRequestParams): Promise<T> => {
        const responseFormat = (format && this.format) ?? void 0;

        if (type === 'multipart/form-data' && body && body !== null && typeof body === 'object') {
            if (params.headers) {
                params.headers.common = 'Accept: */*';
                params.headers.post = '';
                params.headers.put = '';
            }

            body = this.createFormData(body as Record<string, unknown>);
        }

        return this.instance
            .request({
                ...params,
                headers: {
                    ...(type && type !== 'multipart/form-data' ? { 'Content-Type': type } : {}),
                    ...(params.headers ?? {})
                },
                params: query,
                responseType: responseFormat,
                data: body,
                url: path,
                topMostError: new Error('Enhanced stack trace')
            })
            .then((response) => {
                if (this.onResponseHeaders) {
                    this.onResponseHeaders(response.headers);
                }
                const handler = this.successHandler<T> ?? ((res) => res.data as T);
                return handler(response);
            })
            .catch(
                this.axiosErrorHandler<E>((res) => {
                    if (res.type === 'axios-error') {
                        const error = res.error as AxiosError;
                        error.config = {
                            headers: error.config?.headers as AxiosRequestHeaders,
                            method: error.config?.method,
                            data: error.config?.data,
                            baseURL: error.config?.baseURL
                        };
                    }
                    throw res.error;
                })
            ) as Promise<T>;
    };
}
